import React from "react";
import styles from "./ResultsHeader.module.scss";
import { SortOption, ViewMode } from "../../types/SearchData";
import { FilterIcon, GridIcon, ListIcon, PlusIcon } from "../../../../Interfaces/Icons";
import { clearTag } from '../../helpers/Utils';

interface ResultsHeaderProps {
  viewModeOptions: ViewMode[];
  itemsPerPageOptions: string[];
  sortOptions: SortOption[];
  selectedViewMode: ViewMode;
  setSelectedViewMode: (mode: ViewMode) => void;
  selectedResultsPerPage: number;
  setSelectedResultsPerPage: (count: number) => void;
  selectedSortOption: number;
  setSelectedSortOption: (option: number) => void;
  totalResults: number;
  selectedFilters: { [key: string]: string | string[] };
  clearFilter: (filterId: string, value: string) => void; // Updated to include value
  handleShowFilters: () => void; // Added handleShowFilters
  labels: { [key: string]: string };
  showFilters: boolean;
  isMobile: boolean;
}

const ResultsHeader: React.FC<ResultsHeaderProps> = ({
  viewModeOptions,
  itemsPerPageOptions,
  sortOptions,
  selectedViewMode,
  setSelectedViewMode,
  selectedResultsPerPage,
  setSelectedResultsPerPage,
  selectedSortOption,
  setSelectedSortOption,
  totalResults,
  selectedFilters,
  clearFilter,
  handleShowFilters, // Destructure handleShowFilters
  labels,
  showFilters,
  isMobile
}) => {
  const renderFilterTags = () => {
    return Object.entries(selectedFilters)?.map(([filterId, value]) => {
      if (Array.isArray(value)) {
        return value?.map((val) => {
            var displayValue = filterId == "category" ? (val.includes(";") && val.split(";").length > 1 ? val.split(";")[1] : (val === ";" ? "" : val)) :
                filterId == "keywords" ? clearTag(val) :val;
          return (
            <a
              key={`${filterId}-${val}`}
              href="#"
              className={styles["filter-tag"]}
              onClick={(e) => {
                e.preventDefault();
                clearFilter(filterId, val); // Pass specific value to clear
              }}
            >
              {displayValue} <i className="bi bi-x"></i>
            </a>
          );
        });
      }
      return (
        <a
          key={filterId}
          href="#"
          className={styles["filter-tag"]}
          onClick={(e) => {
            e.preventDefault();
            clearFilter(filterId, value as string); // Pass specific value to clear
          }}
        >
          <i className="bi bi-x"></i> {value}
        </a>
      );
    });
  };

  const renderIcon = (viewMode) => {
    switch (viewMode) {
      case "resultsLayout_Grid":
        return <GridIcon />;
      case "resultsLayout_Collapse":
        return <PlusIcon />;
      default:
        return <ListIcon />;
    }
  };

  return (
    <React.Fragment>
      {/* Search Results Header Section */}
      <section className={`${styles["search-results-header"]} section`}>
          <div className={styles["active-filters"]}>
            <div className={styles["filter-tags"]}>{renderFilterTags()}</div>
          </div>

          {/* Results and Controls */}
          {isMobile && 
            <div className={`${styles["results-count"]} pt-2 mb-2`}>
                <div>{labels["ShowingResultsLabel"].replace("{N}", totalResults.toString())}</div>
            </div>
          }
          <div className={styles["results-controls"]}>
          {showFilters && <div className={`${styles["show-filters-button-wrapper"]} ${styles["controls-wrapper"]} d-lg-none`}>
                    <button
                        type="button"
                        className={styles["show-filters-button"]}
                        aria-label="Show Filters"
                        onClick={handleShowFilters}
                    >
                        <FilterIcon /> {labels["FilterMobileButtonLabel"]}
                    </button>
                </div>}
            {isMobile && <div className="seperator-bar"></div>}
            <div className={styles["controls-wrapper"]}>
              <div className={styles["results-count-wrapper"]}>
                  {!isMobile && <div className={styles["results-count"]}>
                    <div>{labels["ShowingResultsLabel"].replace("{N}", totalResults.toString())}</div>
                  </div>}
                  {/* Items Per Page */}
                  {itemsPerPageOptions && itemsPerPageOptions.length > 1 && (
                    <div className={`dropdown ${styles["items-per-page"]}`}>
                      {!isMobile && <div className="seperator-bar"></div>}
                      <a
                        className={styles["control-label"]}
                        href="#"
                        role="button"
                        id="dropdownRowsPerPage"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        {labels["RowsPerPageLabel"]} <span>{selectedResultsPerPage}</span>
                        <i className="fa-solid fa-chevron-down"></i>
                      </a>
                      <div className={`dropdown-menu`} aria-labelledby="dropdownRowsPerPage" id="itemsSelect">
                        {itemsPerPageOptions.map((option) => {
                          const isActive = selectedResultsPerPage == parseInt(option) ? "active" : "";
                          return (
                            <a
                              className={`dropdown-item ${isActive}`}
                              key={option}
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedResultsPerPage(parseInt(option));
                              }}
                            >
                              {option}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
            </div>
            {isMobile && <div className="seperator-bar"></div>}
            <div className={`${styles["controls-wrapper"]}`}>
              <div className={styles["buttons-wrapper"]}>
                <div className={styles["sort-view-wrapper"]}>
                  {viewModeOptions && viewModeOptions.length > 1 ? (
                    <div className={styles["view-options"]}>
                      {viewModeOptions.map((viewMode) => (
                        <button
                          key={viewMode.mode}
                          type="button"
                          className={`${styles["view-btn"]} ${
                            selectedViewMode.mode === viewMode.mode ? styles["active"] : ""
                          }`}
                          aria-label={`${viewMode.displayName} view`}
                          onClick={() => setSelectedViewMode(viewMode)}
                        >
                          {renderIcon(viewMode.mode)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    ""
                  )}
                  {/* Sort Options */}
                  {sortOptions && sortOptions.length > 1 && (
                    <div className={`dropdown sort-options ${styles["sort-options"]}`}>
                      <a
                        className={`sort-label ${styles["sort-label"]}`}
                        href="#"
                        role="button"
                        id="dropdownSortOptions"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        {labels["SortLabel"]} <span>{sortOptions?.at(selectedSortOption).displayName}</span>
                        <i className="fa-solid fa-chevron-down"></i>
                      </a>
                      <div className={`dropdown-menu`} aria-labelledby="dropdownSortOptions">
                        {sortOptions.map((sortOption, index) => {
                          const isActive = selectedSortOption == index ? "active" : "";
                          return (
                            <a
                              className={`dropdown-item ${isActive}`}
                              key={index}
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedSortOption(index);
                              }}
                            >
                              {sortOption.displayName}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
      </section>
      {/* /Search Results Header Section */}
    </React.Fragment>
  );
};

export default ResultsHeader;
